/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { DeliveryType, type Promotion } from '@ahm/api-wrappers-core';
import { useLocationStore } from '@ahm/react-map/store';
import {
  ActionIcon,
  Button,
  CloseIcon,
  Group,
  Paper,
  Space,
  Stack,
  Title,
  rem,
} from '@mantine/core';
import { closeModal, type ContextModalProps } from '@mantine/modals';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ModalName } from '../..';
import { usePromotionEstimate } from '../hooks/use-promotion-estimate';
import InputPromo from './input-promo';
import ListPromos from './list-promos';
import { ROUTES } from '@/config/router';
import { useGetInfinitePromotions } from '@/features/booking/api/promotion/use-get-list-promos';
import { usePromoCode } from '@/features/booking/hooks/use-booking-promotion';
import { ActionIndexPath } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type PromosProps = {
  isClearPromos?: boolean;
};

export default function Promos({ innerProps }: ContextModalProps<PromosProps>) {
  const pathname = usePathname();
  const router = useRouter();
  const isClearPromos = innerProps?.isClearPromos ?? false;

  const tBooking = useTranslations('booking');
  const tAction = useTranslations('common.actions');
  const isMobile = useIsMobile();
  const { paramPromo } = usePromoCode();

  const { applyPromoCodesWithEstimate } = usePromotionEstimate();

  const {
    estimatedData,
    paymentMethod: selectedPaymentMethod,
    selectedService,
    errorPromo,
    setErrorPromo,
  } = useBookingStore((state) => state);

  const paths = useLocationStore.use.paths();
  const pickupPath = paths[ActionIndexPath.Pickup];

  const deliveryTypes = useMemo(() => {
    if (pathname === ROUTES.home || pathname === ROUTES.newOrder) {
      return [DeliveryType.Instant, DeliveryType.SameDay];
    } else {
      return selectedService?.delivery_type;
    }
  }, [pathname, selectedService?.delivery_type]);

  const queryParams = useMemo(
    () => ({
      lat: pickupPath ? pickupPath.lat : 0,
      lng: pickupPath ? pickupPath.lng : 0,
      payment_method: selectedPaymentMethod?.code ?? '',
      service_id: selectedService?._id ?? '',
      service_fee: (estimatedData?.data as any)?.service_fee ?? 0,
      distance: estimatedData?.data?.distance ?? 0,
      order_fee: estimatedData?.data?.subtotal_price ?? 0,
      applied_promo_code_ids: paramPromo ?? [],
      delivery_types: deliveryTypes ?? undefined,
    }),
    [
      pickupPath,
      selectedPaymentMethod?.code,
      selectedService?._id,
      estimatedData?.data,
      paramPromo,
      deliveryTypes,
    ]
  );

  const {
    data: promotionCodeList,
    isLoading: isLoadingPromotions,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetInfinitePromotions(queryParams);

  // Use nuqs for URL state management
  const [urlPromoIds, setUrlPromoIds] = useQueryState(
    'promo_code',
    parseAsArrayOf(parseAsString).withDefault([])
  );

  // State for managing selected voucher IDs and their categories
  const [selectedVoucherIds, setSelectedVoucherIds] = useState<string[]>([]);

  // Initialize selected vouchers from URL parameters when modal opens
  useEffect(() => {
    if (isClearPromos) {
      void setUrlPromoIds([]);
      return;
    }

    if (urlPromoIds.length > 0) {
      setSelectedVoucherIds(urlPromoIds);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClearPromos, urlPromoIds.length]);

  const toggleVoucher = (voucher: Promotion, allVouchers: Promotion[]) => {
    setSelectedVoucherIds((prev) => {
      const next = new Set(prev);

      // Case 1: Deselect if already chosen
      if (next.has(voucher._id)) {
        next.delete(voucher._id);
        return Array.from(next);
      }

      // Case 2: Ensure only one voucher per category
      if (voucher.category) {
        allVouchers.forEach((v) => {
          if (v.category === voucher.category) {
            next.delete(v._id);
          }
        });
      }

      // Case 3: Remove incompatible vouchers
      if ((voucher as any)?.incompatible_with?.length) {
        allVouchers.forEach((v) => {
          if ((voucher as any)?.incompatible_with?.includes(v.category || '')) {
            next.delete(v._id);
          }
        });
      }

      // Finally add the new voucher
      next.add(voucher._id);

      return Array.from(next);
    });
  };

  const isIgnorePromo = useMemo(() => {
    // RULE: ignore if no vouchers selected
    const isEmptyList = promotionCodeList?.pages?.every(
      (page) => page.length === 0
    );
    if (isEmptyList) return true;

    const allPromoIds = promotionCodeList?.pages
      .flat()
      .map((promo) => promo._id);

    const hasAnySelectedInList = selectedVoucherIds.some((id) =>
      allPromoIds?.includes(id)
    );
    if (!hasAnySelectedInList && selectedVoucherIds.length > 0) return true;

    return selectedVoucherIds.length === 0;
  }, [promotionCodeList?.pages, selectedVoucherIds]);

  const handleApply = useCallback(() => {
    const promoCodeIds = isIgnorePromo ? [] : selectedVoucherIds;
    applyPromoCodesWithEstimate(promoCodeIds);
  }, [isIgnorePromo, selectedVoucherIds, applyPromoCodesWithEstimate]);

  const hasSelectedVoucherError = useMemo(() => {
    if (!promotionCodeList?.pages) return false;

    const allVouchers = promotionCodeList.pages.flat();

    return selectedVoucherIds.some((id) => {
      const voucher = allVouchers.find((v) => v._id === id);
      if (!voucher) return false;

      const discountDetails =
        (estimatedData?.data as any)?.discount_details || [];
      const estimateError = discountDetails.find(
        (d: any) => d.promo_code_id === voucher._id && !d.is_valid
      )?.error_message;

      const voucherError = !voucher.valid && (voucher as any).error_message;

      return !!estimateError || !!voucherError;
    });
  }, [selectedVoucherIds, promotionCodeList?.pages, estimatedData]);

  return (
    <>
      <Group
        justify={'space-between'}
        w="100%"
        px={6}
        bg="white"
        pos={'sticky'}
        top={0}
        right={0}
        left={0}
        style={() => ({
          zIndex: 10,
          flexDirection: isMobile ? 'row-reverse' : 'row',
          borderBottom: `1px solid var(--mantine-color-neutral-2)`,
        })}
      >
        {isMobile ? <Space mx={8} w={rem(44)} /> : null}
        <Title
          px={18}
          py={16}
          order={isMobile ? 5 : 4}
          ta={isMobile ? 'center' : 'left'}
          flex={1}
        >
          {tBooking('promo.promotion_title_modal')}
        </Title>
        {!isMobile ? <Space w={rem(24)} /> : null}
        <ActionIcon
          size={44}
          variant="transparent"
          onClick={() => {
            closeModal(ModalName.Promotion);
            // if (errorPromo) {
            //   router.replace(pathname);
            //   setErrorPromo(null);
            // }
          }}
          style={{ outline: 'none !important' }}
        >
          <CloseIcon color="black" size="26px" />
        </ActionIcon>
      </Group>
      <Stack gap={16} bg="neutral.0" px={rem(24)} py={rem(16)}>
        <InputPromo selectedVoucherIds={selectedVoucherIds} />
        <ListPromos
          promotionsData={{
            data: promotionCodeList,
            isLoading: isLoadingPromotions,
            fetchNextPage,
            hasNextPage,
            isFetchingNextPage,
          }}
          selectedVoucherIds={selectedVoucherIds}
          toggleVoucher={toggleVoucher}
        />
      </Stack>

      <Paper
        px={rem(16)}
        py={rem(12)}
        pos="sticky"
        bottom={0}
        right={0}
        left={0}
        style={{
          zIndex: isMobile ? 1000 : 'auto',
        }}
      >
        <Button
          fullWidth
          mih={rem(48)}
          bg={
            isIgnorePromo || hasSelectedVoucherError ? 'neutral.2' : 'primary.6'
          }
          c={isIgnorePromo || hasSelectedVoucherError ? 'neutral.7' : 'white'}
          onClick={handleApply}
          disabled={hasSelectedVoucherError}
        >
          {isIgnorePromo ? 'Bỏ qua khuyến mãi và tiếp tục' : tAction('apply')}
        </Button>
      </Paper>
    </>
  );
}
